import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useChangeAll } from '../../pages/pro/changeAll/ChangeAllContext';
import ChangeAllReviewModal from '../../pages/pro/changeAll/ChangeAllReviewModal';
import ordersService from '../../services/orders.service';

export interface Note {
  recno: number;
  datetime: string;
  userid: string;
  notes: string;
}

interface NotesPanelProps {
  /** Service call to fetch notes. Pass null when no record exists yet (create mode). */
  fetchNotes: (() => Promise<{ notes: Note[] }>) | null;
  /** Service call to persist a note. Pass null for create mode (notes staged locally). */
  addNote: ((text: string) => Promise<void>) | null;
  /** Ref the parent reads on save to flush staged notes (create mode). */
  pendingNotesRef?: React.RefObject<Note[]>;
  /** Called when the displayed note count changes (useful for tab badges). */
  onNoteCountChange?: (count: number) => void;
  isReadOnly?: boolean;
  currentUser?: string;
}

const formatDateTime = (iso: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
};

const NotesPanel: React.FC<NotesPanelProps> = ({
  fetchNotes: fetchNotesFn,
  addNote: addNoteFn,
  pendingNotesRef,
  onNoteCountChange,
  isReadOnly = false,
  currentUser,
}) => {
  const [notes, setNotes] = useState<Note[]>([]);
  // A failed fetch must not read as "no notes" — a note the user wrote would
  // appear silently gone (2026-08-25 sweep).
  const [notesLoadFailed, setNotesLoadFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [optimisticNotes, setOptimisticNotes] = useState<Note[]>([]);

  // Create-mode staging, seeded from parent ref so pending notes survive tab switches
  const [pendingNotes, setPendingNotes] = useState<Note[]>(
    () => pendingNotesRef?.current || [],
  );

  // Change All (Phase 2): when the Notes tab is rendered inside a
  // ChangeAllProvider with enabled=true, offer a "bulk append this note to all
  // target pros" affordance next to Save Note. Outside a provider useChangeAll()
  // returns a disabled no-op, so this is dormant for every other NotesPanel use
  // (LC dashboard, order-management, create mode, etc.).
  const changeAll = useChangeAll();
  const [bulkOpen, setBulkOpen] = useState(false);
  // Text captured at open time so it stays stable while the modal runs even after
  // the textarea is cleared.
  const [bulkText, setBulkText] = useState('');

  // Keep refs to service functions so the mount effect doesn't re-fire on closure changes
  const fetchRef = useRef(fetchNotesFn);
  fetchRef.current = fetchNotesFn;
  const addRef = useRef(addNoteFn);
  addRef.current = addNoteFn;

  // Fetch notes on mount (component is conditionally rendered, so mount = tab activated)
  useEffect(() => {
    const fn = fetchRef.current;
    if (!fn) return;
    let cancelled = false;
    setLoading(true);
    fn()
      .then((res) => {
        if (!cancelled) {
          setNotes(res.notes || []);
          setNotesLoadFailed(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to load notes:', err);
          setNotesLoadFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep parent ref in sync with pending notes
  useEffect(() => {
    if (pendingNotesRef) pendingNotesRef.current = pendingNotes;
  }, [pendingNotes, pendingNotesRef]);

  // Notify parent of count changes
  const totalCount = (fetchNotesFn ? notes.length + optimisticNotes.length : pendingNotes.length);
  useEffect(() => {
    onNoteCountChange?.(totalCount);
  }, [totalCount, onNoteCountChange]);

  const displayNotes = fetchNotesFn
    ? [
        ...optimisticNotes,
        ...notes.filter((n) => !optimisticNotes.some((o) => o.recno === n.recno)),
      ]
    : pendingNotes;

  const handleSave = async () => {
    const trimmed = newNote.trim();
    if (!trimmed) return;
    setSaving(true);
    setNewNote('');

    if (addRef.current) {
      // Existing record: optimistic insert → API → refresh
      const optimistic: Note = {
        recno: -Date.now(),
        datetime: new Date().toISOString(),
        userid: currentUser || 'you',
        notes: trimmed,
      };
      setOptimisticNotes((prev) => [optimistic, ...prev]);
      try {
        await addRef.current(trimmed);
        if (fetchRef.current) {
          const res = await fetchRef.current();
          setNotes(res.notes || []);
        }
        setOptimisticNotes([]);
      } catch {
        setOptimisticNotes((prev) => prev.filter((n) => n.recno !== optimistic.recno));
      } finally {
        setSaving(false);
      }
    } else {
      // Create mode: stage locally
      const staged: Note = {
        recno: -Date.now(),
        datetime: new Date().toISOString(),
        userid: currentUser || 'you',
        notes: trimmed,
      };
      setPendingNotes((prev) => [staged, ...prev]);
      setSaving(false);
    }
  };

  // Reload the persisted notes list (reuses the current fetch fn); used after a
  // bulk change-all append so the anchor pro's list reflects the new note.
  const refetchNotes = useCallback(async () => {
    const fn = fetchRef.current;
    if (!fn) return;
    try {
      const res = await fn();
      setNotes(res.notes || []);
    } catch (err) {
      console.error('Failed to reload notes:', err);
    }
  }, []);

  // Open the change-all review modal, capturing the typed note so it survives the
  // textarea being cleared while the modal runs.
  const openBulk = () => {
    const trimmed = newNote.trim();
    if (!trimmed) return;
    setBulkText(trimmed);
    setBulkOpen(true);
  };

  // Clear the textarea + captured text and refetch so the anchor's list picks up
  // the new note. Used by both the apply and close callbacks below.
  const resetBulk = () => {
    setNewNote('');
    setBulkText('');
    void refetchNotes();
  };
  // Apply finished: reset, but keep the modal open so its results step shows
  // (the modal's own "Done" button then fires onClose to dismiss it).
  const onBulkApplied = () => resetBulk();
  // Modal dismissed (Cancel or Done): close it and reset.
  const onBulkClose = () => {
    setBulkOpen(false);
    resetBulk();
  };

  return (
    <div className="space-y-4">
      {/* Add note */}
      {!isReadOnly && (
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <label className="block text-xs font-medium text-muted mb-1">
            Add Note
          </label>
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500 resize-y"
            placeholder="Type a note..."
            disabled={saving}
          />
          <div className="flex justify-end gap-2 mt-2">
            {changeAll.enabled && (
              <button
                type="button"
                onClick={openBulk}
                disabled={saving || !newNote.trim()}
                title="Append this note to every pro in the Change All set"
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply to all {changeAll.targetPros.length} pros
              </button>
            )}
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !newNote.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Note'}
            </button>
          </div>
        </div>
      )}

      {changeAll.enabled && (
        <ChangeAllReviewModal
          isOpen={bulkOpen}
          pros={changeAll.targetPros}
          title="Change All — Notes"
          // The notes arm already writes templatenotes for a T-pronumber, so this
          // modal is reachable from the Templates board — say the right word.
          recordNoun={changeAll.isTemplate ? 'template' : 'pro'}
          summary={(
            <span>
              Append this note to {changeAll.targetPros.length}{' '}
              {changeAll.isTemplate ? 'template(s)' : 'pro(s)'}
            </span>
          )}
          previewFn={(pros, signal) =>
            ordersService.changeAllNotesPreview({ pros, notes: bulkText }, signal)
          }
          applyFn={(chunk, nonce) =>
            ordersService.changeAllNotesApply(
              { pros: chunk, notes: bulkText, submitNonce: nonce },
              undefined,
            )
          }
          onClose={onBulkClose}
          onApplied={onBulkApplied}
        />
      )}

      {/* Notes list */}
      {loading && displayNotes.length === 0 ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <p className="ml-3 text-sm text-gray-600 dark:text-gray-400">Loading notes...</p>
        </div>
      ) : displayNotes.length === 0 ? (
        <p className="text-sm text-muted text-center py-8">
          {notesLoadFailed
            ? 'Couldn\'t load notes — the request failed. Reopen the tab to retry.'
            : 'No notes found.'}
        </p>
      ) : (
        <div className="space-y-3">
          {displayNotes.map((note) => (
            <div
              key={note.recno}
              className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4${note.recno < 0 ? ' opacity-70' : ''}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-label uppercase">
                  {note.userid}
                </span>
                <span className="text-xs text-muted">
                  {formatDateTime(note.datetime)}
                </span>
              </div>
              <p className="text-sm text-heading whitespace-pre-wrap break-words">
                {note.notes}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotesPanel;
